import React, { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import { ApiService } from 'services';
import TableLoader from 'components/TableLoader';

interface RemitoStatus {
  numeroRemito: number;
  mesPresentacion: string;
  idObraSocial: number;
  obraSocial: string;
  digitoObraSocial: string;
  billingFile: string | null;
  billingFileStatus: 'ok' | 'missing' | 'not_generated';
  fileExists: boolean;
}

interface DeliveryNoteStatusResponse {
  status: string;
  payload: {
    mesPresentacion: string;
    total: number;
    ok: number;
    missing: number;
    notGenerated: number;
    remitos: RemitoStatus[];
  };
}

export default function DeliveryNoteStatus() {
  const currentDate = new Date();
  const currentMonth = `${currentDate.getMonth() + 1}/${currentDate.getFullYear()}`;

  const [mesPresentacion, setMesPresentacion] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DeliveryNoteStatusResponse['payload'] | null>(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res: DeliveryNoteStatusResponse = await ApiService.post('remitos/status', { mesPresentacion });
      if (res.status === 'ok') {
        setData(res.payload);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ok':
        return <Badge color="success">OK</Badge>;
      case 'missing':
        return <Badge color="danger">Faltante</Badge>;
      case 'not_generated':
        return <Badge color="warning">No generado</Badge>;
      default:
        return <Badge color="secondary">{status}</Badge>;
    }
  };

  return (
    <Card className="shadow mb-4">
      <CardBody>
        <h3>Estado de Remitos</h3>
        <small>
          Consulta el estado de los archivos de facturación de los remitos generados para un mes de presentación.
        </small>

        <FormGroup row className="mt-3">
          <Col sm={2}>
            <Label for="mes-presentacion">Mes Presentación</Label>
          </Col>
          <Col sm={3}>
            <Input
              type="text"
              id="mes-presentacion"
              placeholder="MM/YYYY"
              value={mesPresentacion}
              onChange={e => {
                const val = e.target.value;
                const match = val.match(/^(\d{1,2})(\/(\d{0,4}))?$/);
                if (val === '' || match) {
                  setMesPresentacion(val);
                }
              }}
              onBlur={() => {
                const match = mesPresentacion.match(/^(\d{1,2})\/(\d{4})$/);
                if (match) {
                  setMesPresentacion(match[1].padStart(2, '0') + '/' + match[2]);
                }
              }}
              disabled={loading}
            />
          </Col>
          <Col sm={2}>
            <Button
              color="primary"
              onClick={fetchStatus}
              disabled={loading || !mesPresentacion}
            >
              {loading ? 'Consultando...' : 'Consultar'}
            </Button>
          </Col>
        </FormGroup>

        {data && (
          <>
            <Row className="mt-4 mb-3">
              <Col sm={3}>
                <Card className="bg-light">
                  <CardBody className="py-2 text-center">
                    <h5 className="mb-0">{data.total}</h5>
                    <small>Total</small>
                  </CardBody>
                </Card>
              </Col>
              <Col sm={3}>
                <Card className="bg-success text-white">
                  <CardBody className="py-2 text-center">
                    <h5 className="mb-0">{data.ok}</h5>
                    <small>OK</small>
                  </CardBody>
                </Card>
              </Col>
              <Col sm={3}>
                <Card className="bg-danger text-white">
                  <CardBody className="py-2 text-center">
                    <h5 className="mb-0">{data.missing}</h5>
                    <small>Faltantes</small>
                  </CardBody>
                </Card>
              </Col>
              <Col sm={3}>
                <Card className="bg-warning">
                  <CardBody className="py-2 text-center">
                    <h5 className="mb-0">{data.notGenerated}</h5>
                    <small>No generados</small>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  <th>N° Remito</th>
                  <th>Obra Social</th>
                  <th>Dígito</th>
                  <th>Archivo</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center">
                      <TableLoader />
                    </td>
                  </tr>
                ) : data.remitos.length ? (
                  data.remitos.map(remito => (
                    <tr key={remito.numeroRemito}>
                      <td>{remito.numeroRemito}</td>
                      <td>{remito.obraSocial}</td>
                      <td>{remito.digitoObraSocial || '-'}</td>
                      <td>
                        <small>{remito.billingFile || '-'}</small>
                      </td>
                      <td>{getStatusBadge(remito.billingFileStatus)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center">
                      No se encontraron remitos para el mes indicado.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        )}
      </CardBody>
    </Card>
  );
}
